// Local cache of the Firestore appData mirror — the "have both" design
// (Eddie, 2026-08-01): first paint hydrates INSTANTLY from the last visit's
// copy, the fresh fetch reconciles in the background, and a stale number
// corrects itself a second later instead of persisting until the next visit.
// The number is briefly OLD, never wrong, and it self-corrects — which is the
// honesty rule applied to boot time.
//
// Offline degrades to STALE, not broken: a failed background fetch leaves the
// cached (or baked) data in place and writes nothing.
import { ref } from 'vue';

/**
 * Bump when the shape of the cached payload changes incompatibly. Reads only
 * accept an exactly-matching version, and every other yp:mirror:* key is
 * purged on write — so a schema change can never leave a member hydrating an
 * old shape, and abandoned versions don't accumulate in localStorage.
 */
export const MIRROR_SCHEMA_VERSION = 1;

const keyFor = (name) => `yp:mirror:v${MIRROR_SCHEMA_VERSION}:${name}`;
const KEY_PREFIX = 'yp:mirror:';

/**
 * Reactive tick, bumped on every post-mount hydrate. LIBRARY_FIXTURES is a
 * PLAIN array mutated in place — deliberately, so module-level references
 * stay valid — which means Vue cannot see a reconciliation happen. Pre-mount
 * hydration never needed it (mutation preceded every read); in-place
 * reconciliation does. Only the keep-alive'd home must subscribe: every other
 * page remounts on navigation and re-reads the fixtures fresh.
 */
export const mirrorTick = ref(0);

export function bumpMirrorTick() {
  mirrorTick.value += 1;
}

/** A cacheable payload: a non-empty array (expressions, masterclasses) or a
 *  non-empty plain object (the engine content bundle). */
function validPayload(p) {
  if (Array.isArray(p)) return p.length > 0;
  return !!p && typeof p === 'object' && Object.keys(p).length > 0;
}

/** Cached payload for `name`, or null. Wrong/old versions read as misses. */
export function readMirrorCache(name) {
  try {
    const raw = localStorage.getItem(keyFor(name));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return validPayload(parsed) ? parsed : null;
  } catch (e) {
    return null; // corrupt entry or no storage — behave as a cache miss
  }
}

/** Write-through after a successful fetch; purges every other version's keys. */
export function writeMirrorCache(name, items) {
  if (!validPayload(items)) return;
  try {
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(KEY_PREFIX) && !k.includes(`:v${MIRROR_SCHEMA_VERSION}:`)) {
        localStorage.removeItem(k);
      }
    }
    localStorage.setItem(keyFor(name), JSON.stringify(items));
  } catch (e) { /* quota/private mode — next load just pays the fetch again */ }
}

/**
 * Cheap identity for "did the fresh fetch differ" — length + a stable stringify.
 * ~190KB stringify runs once per load in the background; milliseconds.
 */
export function sameMirrorPayload(a, b) {
  if (!validPayload(a) || !validPayload(b)) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && a.length !== b.length) return false;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch (e) { return false; }
}
